import range from 'lodash/range';
import { useContext, useEffect, useRef } from 'react';
import { useTranslate } from 'react-t7e';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { H1 } from 'src/components/common/typography/Header';
import { Countries } from 'src/graphql/schema.graphql';
import { TranslatedItem } from 'src/hooks/directus/useTranslateItem';
import { useSpeakersQuery } from 'src/modules/experts/hooks/useSpeakersQuery';
import {
  SpeakerTile,
  SpeakerTileSkeleton,
} from 'src/modules/materials/components/SpeakerTile';
import { LayoutRegisterContext } from 'src/providers/LayoutRegisterProvider';
import { media } from 'src/styles/designs';
import styled from 'styled-components';

export interface LocalExpertsCountryDetailsProps {
  country: TranslatedItem<Countries>;
}

const Container = styled.main`
  position: relative;
  padding: 50px 0;
`;

const SpeakersContainer = styled.div`
  display: grid;

  ${media.w.greaterThanOrEqual.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ScrollAnchor = styled.div`
  position: absolute;
  top: calc(-1 * var(--header-static-height));
  left: 0;
`;

export const LocalExpertsCountryDetails = ({
  country,
}: LocalExpertsCountryDetailsProps) => {
  const { _ } = useTranslate();
  const { data: speakers, isLoading } = useSpeakersQuery({
    filters: {
      serviceCountries: {
        countries_id: {
          slug: { _eq: country.slug },
        },
      },
    },
  });

  const ctx = useContext(LayoutRegisterContext);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [country, ctx?.ui.height.header.static]);

  return (
    <Container>
      <ScrollAnchor ref={scrollRef} />
      <ConstraintContainer>
        <H1 as="h2" $nail="outside">
          {_('Experts in {country}', undefined, { country: country.name })}
        </H1>

        <SpeakersContainer>
          {isLoading
            ? range(3).map((num) => <SpeakerTileSkeleton key={num} />)
            : speakers?.map((speaker) => (
                <SpeakerTile
                  key={speaker.id}
                  speaker={speaker}
                  variant="secondary"
                />
              ))}
        </SpeakersContainer>
      </ConstraintContainer>
    </Container>
  );
};
